// Settings in the product (0.W, 0.12): what each plain form shows and saves. A form reads its fields from the approved
// settings, and saves only the fields that changed as one section change (act propose_settings_change). The database
// builds the new version from the approved one; the owner approves it on the same screen (decide_config).
import type { ConfigVersion, Json, JsonObject } from "./types";
import {
  AD_PLATFORMS,
  CTA_KINDS,
  FIRST_ASKS,
  FIRST_STEPS,
  FLAVOURS,
  NETWORKS,
  OFFER_AFTER,
  OFFERS,
  PUBLISH_TO,
  ROUTES,
  SAVE_OFFER_APPROVAL,
  SAVE_OFFER_KINDS,
  SIGNAL_PLAYS,
  SYNDICATE,
  SettingsRefusal,
  validateSettingsChange,
} from "./settingsRules";

export type SettingsSection =
  | "ctas"
  | "connect_routes"
  | "connect_paths"
  | "connect_booking"
  | "connect_flavours"
  | "connect_save_offer"
  | "social"
  | "newsletter"
  | "content"
  | "listings"
  | "ads"
  | "network"
  | "email_times"
  | "speed_to_lead";

/** The forms, each shown on its module's Rules page (Settings › Your settings links there). `anchor` is the #id each form lives at. */
export const SETTINGS_FORMS: { key: SettingsSection; title: string; blurb: string; anchor: string; module: string }[] = [
  { key: "ctas", title: "Calls to action", blurb: "The one thing each post, article and email asks people to do, and where it may lead.", anchor: "ctas", module: "Attract" },
  { key: "connect_routes", title: "Connect routes", blurb: "What happens first when someone answers a call to action.", anchor: "routes", module: "Connect" },
  { key: "connect_paths", title: "Connect paths", blurb: "The steps each offer goes through from interested to yes. You change them on Conversations › Routes.", anchor: "paths", module: "Connect" },
  { key: "connect_booking", title: "Booking", blurb: "Your meeting types, the Ogma booking page, text reminders and Calendly or Cal.com. You change them on Settings › Campaign defaults › Meeting types and Settings › Meetings › Booking links.", anchor: "booking", module: "Connect" },
  { key: "connect_flavours", title: "Connect pace", blurb: "For each way people come in: how fast you reply, what you ask for first, and when an offer may be made.", anchor: "pace", module: "Connect" },
  { key: "connect_save_offer", title: "Save offer", blurb: "Whether Ogma makes one last offer before a deal is lost, what it may be, and when you approve it.", anchor: "save-offer", module: "Connect" },
  { key: "social", title: "Social", blurb: "The accounts the engine posts to, how often, and the best times.", anchor: "social", module: "Attract" },
  { key: "newsletter", title: "Newsletter", blurb: "Whether you send one, who it comes from, and where it is shared.", anchor: "newsletter", module: "Attract" },
  { key: "content", title: "Content", blurb: "The monthly article, its topics and news searches, and where articles live.", anchor: "content", module: "Attract" },
  { key: "listings", title: "Get found", blurb: "Directory listings for your niche, and asking happy customers for reviews.", anchor: "get-found", module: "Attract" },
  { key: "ads", title: "Ads", blurb: "Whether ads run, where, and the most they may spend in a month.", anchor: "ads", module: "Attract" },
  { key: "network", title: "Network", blurb: "When happy customers are asked for an introduction.", anchor: "network", module: "Network" },
  { key: "email_times", title: "When emails go out", blurb: "The times your cold emails are sent, on whose clock, how far apart, and a test of two send times.", anchor: "send-times", module: "Find" },
  { key: "speed_to_lead", title: "Speed to lead", blurb: "The instant reply to every enquiry, telling you at once, and how fast a personal reply should follow. You change it on Settings › Campaign defaults › Speed to lead.", anchor: "speed-to-lead", module: "Connect" },
];

/** Where each field lives in the settings (the same list as crm__settings_fields in migrations 20261110140000,
 * 20261122090000, 20261125090000 and 20261126090000). */
export const FIELD_PATHS: Record<SettingsSection, Record<string, string[]>> = {
  ctas: { required: ["ctas", "required"], default_offer: ["ctas", "default_offer"], allowed_hosts: ["ctas", "allowed_hosts"], library: ["ctas", "library"] },
  connect_routes: Object.fromEntries(ROUTES.map((r) => [r, ["connect", "routes", r, "first"]])),
  connect_paths: { paths: ["connect", "paths"] },
  // 7.8-7.12 booking (migration 20261130090000)
  connect_booking: {
    page_on: ["connect", "booking", "page"], page_url: ["connect", "booking", "page_url"],
    offer_after_enquiry: ["connect", "booking", "offer_after_enquiry"], sms_reminders: ["connect", "booking", "sms_reminders"],
    sms_sender: ["connect", "booking", "sms_sender"], calendly: ["connect", "booking", "calendly"], cal_com: ["connect", "booking", "cal_com"],
    meeting_types: ["connect", "booking", "meeting_types"],
  },
  connect_flavours: Object.fromEntries(
    FLAVOURS.flatMap((f) => (["reply_within_hours", "first_ask", "offer_after", "touches_before_offer"] as const).map((k) => [`${f}_${k}`, ["connect", "flavours", f, k]])),
  ),
  connect_save_offer: Object.fromEntries(["enabled", "kinds", "max_discount_pct", "approval", "approve_above_pct", "decide_after_days"].map((k) => [k, ["connect", "save_offer", k]])),
  social: { networks: ["social", "networks"], cadence_per_week: ["social", "cadence_per_week"], best_times: ["social", "best_times"], publisher: ["social", "publisher"] },
  newsletter: { enabled: ["newsletter", "enabled"], from_name: ["newsletter", "from_name"], signup_url: ["newsletter", "signup_url"], archive_url: ["newsletter", "archive_url"], syndicate: ["newsletter", "syndicate"] },
  content: {
    pillar_words: ["content", "pillar", "words"],
    pillar_posts: ["content", "pillar", "posts"],
    topics: ["content", "news", "keywords"],
    news_queries: ["content", "news", "queries"],
    publish_to: ["content", "publish_to"],
    blog_base_url: ["content", "blog", "base_url"],
  },
  listings: { enabled: ["listings", "enabled"], niche: ["listings", "niche"], reviews_enabled: ["listings", "reviews", "enabled"], review_links: ["listings", "reviews", "links"], review_delay_days: ["listings", "reviews", "delay_days"],
    // 10.19 the happiness check before the review ask (migration 20261201090000), kept under reviews.happiness
    happiness_enabled: ["reviews", "happiness", "enabled"], happiness_delay_days: ["reviews", "happiness", "delay_days"] },
  ads: { enabled: ["channels", "ads", "enabled"], platforms: ["channels", "ads", "platforms"], monthly_cap: ["channels", "ads", "monthly_cap"], currency: ["channels", "ads", "currency"], destination: ["channels", "ads", "destination"] },
  network: { referral_after_days: ["network", "referral_after_days"], referral_gap_days: ["network", "referral_gap_days"] },
  email_times: {
    send_window: ["channels", "email", "send_window"],
    min_gap_minutes: ["channels", "email", "min_gap_minutes"],
    send_time_test: ["channels", "email", "send_time_test"],
  },
  // 1.61, 6.12, E56 (docs/specs/SPEED_TO_LEAD.md section 8; migration 20261125090000).
  speed_to_lead: {
    personal_reply_minutes: ["speed_to_lead", "personal_reply_minutes"],
    hours_days: ["speed_to_lead", "hours", "days"],
    hours_start: ["speed_to_lead", "hours", "start"],
    hours_end: ["speed_to_lead", "hours", "end"],
    instant_reply_enabled: ["speed_to_lead", "instant_reply", "enabled"],
    instant_reply_kinds: ["speed_to_lead", "instant_reply", "kinds"],
    instant_reply_channels: ["speed_to_lead", "instant_reply", "channels"],
    instant_reply_max_per_hour: ["speed_to_lead", "instant_reply", "max_per_hour"],
    instant_reply_max_per_day: ["speed_to_lead", "instant_reply", "max_per_day"],
    instant_reply_whatsapp_max_per_day: ["speed_to_lead", "instant_reply", "whatsapp_max_per_day"],
    instant_reply_whatsapp_countries: ["speed_to_lead", "instant_reply", "whatsapp_countries"],
    instant_reply_tally_whatsapp_field: ["speed_to_lead", "instant_reply", "tally_whatsapp_field"],
    bot_check_site_key: ["speed_to_lead", "bot_check", "site_key"],
    instant_reply_subject: ["speed_to_lead", "instant_reply", "subject"],
    instant_reply_in_hours: ["speed_to_lead", "instant_reply", "in_hours"],
    instant_reply_out_of_hours: ["speed_to_lead", "instant_reply", "out_of_hours"],
    new_enquiry_alert: ["alerts", "new_enquiry"],
    reply_window_start: ["channels", "email", "reply_window", "start"],
    reply_window_end: ["channels", "email", "reply_window", "end"],
    reply_window_days: ["channels", "email", "reply_window", "days"],
    reply_reserve: ["channels", "email", "reply_reserve"],
    booking_url: ["scheduling", "booking_url"],
  },
};

/** The instant reply's starting wording (docs/specs/SPEED_TO_LEAD.md section 4; tenants/_template/tenant.yaml). The
 * same text in and out of hours: {reply_by} carries the difference. */
export const DEFAULT_INSTANT_REPLY =
  "Hi {first_name},\n\nThanks for getting in touch with {business}. I've got your message and will reply personally {reply_by}.\n\nIf it's easier to talk it through, you can pick a time that suits you here: {booking_link}\n\n{owner_name}";

/** What the engine uses when a field is not set (tenants/_template/tenant.yaml). */
export const DEFAULTS: Record<SettingsSection, Record<string, Json>> = {
  ctas: { required: true, default_offer: "consult", allowed_hosts: [], library: [] },
  connect_routes: { enquiry: "6.R", book: "7.R", dm: "6.R", call: "human", subscribe: "10.1", download: "6.R", review: "none" },
  connect_paths: { paths: [] },
  // crm__booking_settings: the page off; times offered after an enquiry once it is on; no texts; no tools read in.
  connect_booking: { page_on: false, page_url: "", offer_after_enquiry: true, sms_reminders: false, sms_sender: "", calendly: false, cal_com: false, meeting_types: [] },
  // engine/connect.py and crm__connect_settings: Attract fast, Find steady, Network slow.
  connect_flavours: {
    attract_reply_within_hours: 1, attract_first_ask: "book", attract_offer_after: "first_reply", attract_touches_before_offer: 0,
    find_reply_within_hours: 8, find_first_ask: "call", find_offer_after: "meeting", find_touches_before_offer: 1,
    network_reply_within_hours: 24, network_first_ask: "coffee", network_offer_after: "relationship", network_touches_before_offer: 3,
  },
  connect_save_offer: { enabled: false, kinds: [...SAVE_OFFER_KINDS], max_discount_pct: 10, approval: "always", approve_above_pct: 5, decide_after_days: 7 },
  social: { networks: [], cadence_per_week: 3, best_times: {}, publisher: "buffer" },
  newsletter: { enabled: true, from_name: "", signup_url: "", archive_url: "", syndicate: [] },
  content: { pillar_words: 1200, pillar_posts: 7, topics: [], news_queries: [], publish_to: "ogma_pages", blog_base_url: "" },
  listings: { enabled: false, niche: "", reviews_enabled: false, review_links: {}, review_delay_days: 3, happiness_enabled: false, happiness_delay_days: 7 },
  ads: { enabled: false, platforms: [], monthly_cap: 0, currency: "GBP", destination: "website" },
  network: { referral_after_days: 21, referral_gap_days: 180 },
  // engine/outreach.py: weekdays 08:00-17:00 on each recipient's clock, at least 4 minutes apart, no test.
  email_times: {
    send_window: { windows: [{ days: "weekdays", start: "08:00", end: "17:00" }], timezone: "recipient" },
    min_gap_minutes: 4,
    send_time_test: { enabled: false, slots: [] },
  },
  speed_to_lead: {
    personal_reply_minutes: 15, hours_days: ["mon", "tue", "wed", "thu", "fri"], hours_start: "08:00", hours_end: "18:00",
    instant_reply_enabled: false, instant_reply_kinds: ["enquiry", "scorecard", "chat"], instant_reply_channels: ["whatsapp", "email"],
    instant_reply_max_per_hour: 30,
    instant_reply_max_per_day: 100,
    // review R2: [] means the country of the business's own WhatsApp number (crm__stl_wa_countries)
    instant_reply_whatsapp_max_per_day: 20, instant_reply_whatsapp_countries: [], instant_reply_tally_whatsapp_field: "",
    // bot check (Cloudflare Turnstile): "" = off
    bot_check_site_key: "",
    instant_reply_subject: "Re: your enquiry to {business}", instant_reply_in_hours: DEFAULT_INSTANT_REPLY, instant_reply_out_of_hours: DEFAULT_INSTANT_REPLY,
    new_enquiry_alert: true, reply_window_start: "07:00", reply_window_end: "21:00", reply_window_days: ["mon", "tue", "wed", "thu", "fri"],
    reply_reserve: 5, booking_url: "",
  },
};

// ------------------------------------------------------------------ upgrade sections (Oct 2026)
/** Settings sections added by the Oct 2026 upgrades (Wave 0, migration 20261220010200; docs/specs/UPGRADES_2026_10.md).
 * Act and the database accept them like any other section, but their forms live on the module pages, not in
 * Settings › Your settings, so they are not in SETTINGS_FORMS. Feature code reads and proposes them with readSection,
 * checkSection and propose_settings_change as usual. */
export type UpgradeSection =
  | "find_letters"
  | "find_contact_finders"
  | "find_plays"
  | "find_phone"
  | "connect_notetaker"
  | "connect_payments"
  | "connect_phone_line"
  | "connect_quote_followups"
  | "crm_interactions"
  | "crm_tasks"
  | "attract_search"
  | "attract_pictures"
  | "attract_ai_answers"
  | "newsletter_welcome"
  | "find_channels"
  | "find_pace"
  | "find_linkedin"
  | "connect_video"
  | "connect_hours"
  | "connect_brief"
  | "find_style"
  | "connect_documents"
  | "quote_assistant"
  | "find_countries";
export type AnySection = SettingsSection | UpgradeSection;

const under = (base: string[], keys: string[]) => Object.fromEntries(keys.map((k) => [k, [...base, k]]));

export const UPGRADE_FIELD_PATHS: Record<UpgradeSection, Record<string, string[]>> = {
  find_letters: under(["channels", "letter"], ["enabled", "tool", "monthly_cap_gbp"]),
  find_contact_finders: {
    email_finding: ["tools", "email_finding"], phone_finding: ["tools", "phone_finding"],
  },
  find_plays: { ...under(["plays", "signals"], [...SIGNAL_PLAYS]), heat_half_life_days: ["plays", "heat_half_life_days"] },
  find_phone: {
    tps_provider: ["tools", "tps_screening"], recording: ["channels", "call", "record"],
    calls_on: ["channels", "call", "enabled"], calls_via: ["channels", "call", "provider"],
  },
  connect_notetaker: under(["connect", "notetaker"], ["enabled", "source", "retention_days"]),
  connect_payments: {
    provider: ["connect", "payments", "provider"], deposit_kind: ["connect", "payments", "deposit", "kind"],
    deposit_percent: ["connect", "payments", "deposit", "percent"], deposit_fixed: ["connect", "payments", "deposit", "fixed"],
  },
  connect_phone_line: under(["connect", "phone_line"], ["enabled", "owner_mobile", "ring_seconds", "text_back"]),
  connect_quote_followups: under(["connect", "quote_followups"], ["live_alert", "viewed_days", "unopened_days", "expiry_days"]),
  crm_interactions: under(["crm", "interactions"], ["enabled", "add_people_from_domains", "keep_subject", "blocked_domains"]),
  crm_tasks: { digest: ["crm", "tasks", "digest", "enabled"], digest_time: ["crm", "tasks", "digest_time"], digest_channel: ["crm", "tasks", "digest_channel"] },
  attract_search: { search_console_property: ["content", "search_console", "property"], enabled: ["content", "search_console", "enabled"] },
  attract_pictures: under(["content", "visuals"], ["sources", "monthly_cap_usd"]),
  attract_ai_answers: under(["content", "ai_visibility"], ["competitors", "cadence"]),
  newsletter_welcome: under(["newsletter", "welcome"], ["enabled", "emails", "referral"]),
  // K-06 Sending rules (migration 20261226060000): the kinds of outreach allowed, and the weekly pace.
  find_channels: {
    email: ["channels", "email", "enabled"], by_hand: ["outreach", "by_hand", "enabled"], calls: ["channels", "call", "enabled"],
    letters: ["channels", "letter", "enabled"], linkedin: ["channels", "linkedin", "enabled"], whatsapp: ["channels", "whatsapp", "enabled"],
    // L-02 (migration 20261227020000): social DMs, sent by hand.
    social_dm: ["channels", "social_dm", "enabled"],
  },
  // L-02 (migration 20261227020000): what drives the pace (firms or meetings a week) and the meetings a week.
  find_pace: { weekly_target: ["find", "weekly_target"], mode: ["find", "pace_mode"], meetings_per_week: ["find", "meetings_per_week"] },
  // L-01 Accounts › LinkedIn (migration 20261227010000): the owner's LinkedIn profile, for research and drafts.
  find_linkedin: { profile_url: ["channels", "linkedin", "profile_url"] },
  // 2.21 Sending rules (migration 20270225100200): the countries Ogma may email for the business.
  find_countries: { countries: ["compliance", "auto_outreach_countries"] },
  // Connect walk-through package 6 (migration 20270101090000): the default video tool, where the engine reads it.
  connect_video: { default_video: ["scheduling", "video_link"] },
  // Connect, day one (migration 20270206090000): booking hours, meeting length and calendar reminder; the brief's sections.
  connect_hours: {
    ...under(["scheduling", "working_hours"], ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]),
    meeting_minutes: ["scheduling", "meeting_length_minutes"], reminder_minutes: ["scheduling", "calendar_reminder_minutes"],
  },
  connect_brief: { sections: ["scheduling", "brief", "sections"] },
  // Sending style (migration 20270208090000): where engine/drafting.py and crm_sending_style read them.
  find_style: {
    sender_name: ["channels", "email", "from_name"],
    ...under(["voice"], ["greeting", "sign_off", "tone", "rules", "avoid", "learned_notes"]),
    auto_learn: ["voice", "auto_approve_learned_notes"],
  },
  // After the call (migration 20270209090000): the documents the email after the call can share.
  connect_documents: { documents: ["connect", "documents"] },
  // 9.28 The quote assistant's rules (migration 20270224090000), where crm__assistant_rules reads them.
  quote_assistant: {
    enabled: ["portal", "assistant_enabled"], never: ["portal", "assistant_never"], session_max: ["portal", "assistant_session_max"],
    answer_chars: ["portal", "assistant_answer_chars"], max_per_day: ["portal", "assistant_max_per_day"],
    handoff: ["portal", "assistant_handoff"], model: ["portal", "assistant_model"],
  },
};

/** What the engine uses when an upgrade field is not set. Every feature starts off. */
export const UPGRADE_DEFAULTS: Record<UpgradeSection, Record<string, Json>> = {
  find_letters: { enabled: false, tool: "scribeless", monthly_cap_gbp: 50 },
  find_contact_finders: { email_finding: ["own", "site", "pattern", "hunter"], phone_finding: false },
  find_plays: { ...Object.fromEntries(SIGNAL_PLAYS.map((k) => [k, false])), heat_half_life_days: 14 },
  find_phone: { tps_provider: "file", recording: false, calls_on: false, calls_via: "tel" },
  connect_notetaker: { enabled: false, source: "paste", retention_days: 7 },
  connect_payments: { provider: "stripe", deposit_kind: "none", deposit_percent: 25, deposit_fixed: 0 },
  connect_phone_line: { enabled: false, owner_mobile: "", ring_seconds: 20, text_back: false },
  connect_quote_followups: { live_alert: true, viewed_days: 2, unopened_days: 3, expiry_days: 2 },
  crm_interactions: { enabled: false, add_people_from_domains: false, keep_subject: true, blocked_domains: [] },
  crm_tasks: { digest: false, digest_time: "07:30", digest_channel: "email" },
  attract_search: { search_console_property: "", enabled: false },
  attract_pictures: { sources: ["card"], monthly_cap_usd: 20 },
  attract_ai_answers: { competitors: [], cadence: "weekly" },
  newsletter_welcome: { enabled: false, emails: 3, referral: true },
  // As the engine reads them when unset: by hand on (crm__hand_send_settings), everything else off; 25 new firms a week.
  find_channels: { email: false, by_hand: true, calls: false, letters: false, linkedin: false, whatsapp: false, social_dm: false },
  find_pace: { weekly_target: 25, mode: "firms", meetings_per_week: 2 },
  find_linkedin: { profile_url: "" },
  // Unset, the engine clears the business's own country (crm__auto_outreach_countries; tenant.country, else the UK).
  find_countries: { countries: ["GB"] },
  // As the engine reads scheduling.video_link when unset.
  connect_video: { default_video: "google_meet" },
  // As engine/scheduler.py reads them when unset: 09:00-17:00 on weekdays, 30-minute meetings, a 15-minute reminder.
  connect_hours: {
    mon: ["09:00-17:00"], tue: ["09:00-17:00"], wed: ["09:00-17:00"], thu: ["09:00-17:00"], fri: ["09:00-17:00"], sat: [], sun: [],
    meeting_minutes: 30, reminder_minutes: 15,
  },
  // engine/meetings.py DEFAULT_BRIEF_SECTIONS: a short brief.
  connect_brief: { sections: ["company", "contact", "offer", "outreach", "recent_meetings", "talking_points"] },
  // Unset, the agent writes its own greeting and signs with the sender's name (engine/drafting.py); learned notes wait
  // for approval.
  find_style: { sender_name: "", greeting: "", sign_off: "", tone: "", rules: [], avoid: [], learned_notes: [], auto_learn: false },
  connect_documents: { documents: [] },
  // As crm__assistant_rules and crm_assistant_turn read them when unset.
  quote_assistant: { enabled: true, never: [], session_max: 10, answer_chars: 400, max_per_day: 300, handoff: "task", model: "haiku" },
};

const ALL_PATHS: Record<AnySection, Record<string, string[]>> = { ...FIELD_PATHS, ...UPGRADE_FIELD_PATHS };
const ALL_DEFAULTS: Record<AnySection, Record<string, Json>> = { ...DEFAULTS, ...UPGRADE_DEFAULTS };

function at(config: JsonObject | null | undefined, path: string[]): Json | undefined {
  let node: Json | undefined = config ?? undefined;
  for (const p of path) {
    if (!node || typeof node !== "object" || Array.isArray(node)) return undefined;
    node = (node as JsonObject)[p];
  }
  return node;
}

/** The form's starting values: the approved settings, or the default where a field is not set. */
export function readSection(config: JsonObject | null | undefined, section: AnySection): JsonObject {
  const out: JsonObject = {};
  for (const [field, path] of Object.entries(ALL_PATHS[section])) {
    const v = at(config, path);
    out[field] = v === undefined || v === null ? ALL_DEFAULTS[section][field]! : v;
  }
  return out;
}

const same = (a: Json | undefined, b: Json | undefined) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Only the fields that differ from the starting values: what a form sends. */
export function changedFields(before: JsonObject, after: JsonObject): JsonObject {
  const out: JsonObject = {};
  for (const [k, v] of Object.entries(after)) if (!same(before[k], v)) out[k] = v;
  return out;
}

/** The settings with one section's change made, as the database makes it (crm__jsonb_set_deep). For demo mode. */
export function applySection(config: JsonObject, section: AnySection, value: JsonObject): JsonObject {
  const next = structuredClone(config) as JsonObject;
  for (const [field, v] of Object.entries(value)) {
    const path = ALL_PATHS[section]?.[field];
    if (!path) continue;
    let node = next;
    path.forEach((p, i) => {
      if (i === path.length - 1) node[p] = v;
      else {
        const child = node[p];
        if (!child || typeof child !== "object" || Array.isArray(child)) node[p] = {};
        node = node[p] as JsonObject;
      }
    });
  }
  return next;
}

/** The act function's check, run before saving: null when fine, else what to fix. */
export function checkSection(section: AnySection, value: JsonObject): string | null {
  try {
    validateSettingsChange(section, value);
    return null;
  } catch (e) {
    if (e instanceof SettingsRefusal) {
      // "monthly_cap must be…" reads as "Monthly cap must be…"; list positions count from 1.
      const m = e.message.replace(/^[^\s]+/, (f) => f.replace(/\[(\d+)\]/g, (_x, n: string) => ` ${Number(n) + 1}`).replace(/[_.]/g, " "));
      return `${m.charAt(0).toUpperCase()}${m.slice(1)}.`;
    }
    throw e;
  }
}

// ------------------------------------------------------------------ the waiting change
export interface ChangeInfo {
  section: AnySection;
  fields: string[];
}

export type ConfigVersionWithChange = ConfigVersion & { change?: ChangeInfo | null };

/** The draft waiting for approval, if any, and whether it came from a settings form or from the strategy agent. */
export function waitingDraft(versions: ConfigVersionWithChange[]): { draft: ConfigVersionWithChange; fromForm: boolean } | null {
  const draft = versions.filter((v) => v.status === "draft").sort((a, b) => b.version - a.version)[0];
  return draft ? { draft, fromForm: Boolean(draft.change) } : null;
}

export function approvedVersion(versions: ConfigVersion[]): ConfigVersion | null {
  return versions.filter((v) => v.status === "approved").sort((a, b) => b.version - a.version)[0] ?? null;
}

export function formTitle(section: string | undefined): string {
  return SETTINGS_FORMS.find((f) => f.key === section)?.title ?? "Settings";
}

// ------------------------------------------------------------------ labels
export const OFFER_LABELS: Record<(typeof OFFERS)[number], string> = {
  consult: "Book a call",
  quote: "Ask for a quote",
  guide: "Get a free guide",
  scorecard: "Take a scorecard",
  subscribe: "Join the newsletter",
  visit: "Visit us",
  call: "Phone us",
};
export const CTA_KIND_LABELS: Record<(typeof CTA_KINDS)[number], string> = {
  link: "A link",
  keyword: "A keyword to comment or message",
  qr: "A QR code",
  phone: "A phone number",
  form: "A form on your site",
};
export const ROUTE_LABELS: Record<(typeof ROUTES)[number], string> = {
  enquiry: "Someone sends an enquiry",
  book: "Someone books a call",
  dm: "Someone sends a direct message",
  call: "Someone asks to be called",
  subscribe: "Someone joins the newsletter",
  download: "Someone downloads a guide",
  review: "Someone leaves a review",
};
export const FIRST_STEP_LABELS: Record<(typeof FIRST_STEPS)[number], string> = {
  "6.R": "Reply to them (the inbox agent drafts it)",
  "7.R": "Offer times to meet",
  "10.1": "Keep in touch by newsletter only",
  human: "A task for you to call them",
  none: "Nothing: just count it",
};
export const NETWORK_LABELS: Record<(typeof NETWORKS)[number], string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  x: "X",
  linkedin: "LinkedIn",
  tiktok: "TikTok",
  pinterest: "Pinterest",
  threads: "Threads",
  youtube: "YouTube",
  google_business_profile: "Google Business Profile",
};
/** The account kind a network usually has (social.networks[].account). */
export const DEFAULT_ACCOUNT: Record<(typeof NETWORKS)[number], string> = {
  instagram: "business",
  facebook: "page",
  x: "profile",
  linkedin: "company_page",
  tiktok: "business",
  pinterest: "business",
  threads: "profile",
  youtube: "channel",
  google_business_profile: "listing",
};
export const ACCOUNT_KINDS = ["business", "page", "profile", "personal", "company_page", "channel", "listing", "creator"] as const;
export const SYNDICATE_LABELS: Record<(typeof SYNDICATE)[number], string> = { linkedin: "LinkedIn", substack: "Substack", medium: "Medium", trade_press: "Trade press" };
export const PUBLISH_TO_LABELS: Record<(typeof PUBLISH_TO)[number], string> = {
  ogma_pages: "Ogma's pages for you",
  subfolder_worker: "A folder on your own site (Cloudflare)",
  subdomain: "A subdomain of your site",
  wordpress: "WordPress",
  webflow: "Webflow",
  shopify: "Shopify",
  wix: "Wix",
  ghost: "Ghost",
};
export const AD_PLATFORM_LABELS: Record<(typeof AD_PLATFORMS)[number], string> = { meta: "Meta (Facebook and Instagram)", google: "Google", microsoft: "Microsoft (Bing)", nextdoor: "Nextdoor", linkedin: "LinkedIn" };
export const FLAVOUR_FORM_LABELS: Record<(typeof FLAVOURS)[number], { title: string; hint: string }> = {
  attract: { title: "Attract", hint: "They came to you: reply fast and make the next step easy." },
  find: { title: "Find", hint: "You went to them: earn the meeting first, offer after it." },
  network: { title: "Network", hint: "Introduced or met: the relationship before the offer." },
};
export const FIRST_ASK_LABELS: Record<(typeof FIRST_ASKS)[number], string> = { book: "Book a call", quote: "A quote", call: "A short call", coffee: "A coffee or informal chat" };
export const OFFER_AFTER_LABELS: Record<(typeof OFFER_AFTER)[number], string> = { first_reply: "From the first reply", meeting: "After a meeting", relationship: "Once the relationship is there" };
export const SAVE_OFFER_KIND_LABELS: Record<(typeof SAVE_OFFER_KINDS)[number], string> = { discount: "A discount", smaller_first_job: "A smaller first job", payment_terms: "Payment terms", trial: "A trial" };
export const SAVE_OFFER_APPROVAL_LABELS: Record<(typeof SAVE_OFFER_APPROVAL)[number], string> = {
  always: "I approve every save offer",
  above: "I approve discounts above a limit",
  never: "No approval for the offer (the email still follows my message approvals)",
};
export { AD_PLATFORMS, CTA_KINDS, FIRST_ASKS, FIRST_STEPS, FLAVOURS, NETWORKS, OFFER_AFTER, OFFERS, PUBLISH_TO, ROUTES, SAVE_OFFER_APPROVAL, SAVE_OFFER_KINDS, SYNDICATE };

/** "a, b , c" or one per line → a clean list. */
export function splitList(text: string, byLine = false): string[] {
  return [...new Set(text.split(byLine ? /\n/ : /[\n,]/).map((s) => s.trim()).filter(Boolean))];
}

// ------------------------------------------------------------------ 0.12 the Buffer channel picker
export interface BufferChannel {
  id: string;
  name: string;
  service: string;
  network: string | null;
  organization_id: string;
  organization: string;
  avatar: string | null;
}

export interface ChannelPick {
  on: boolean;
  network: string;
  account: string;
}

/**
 * One social.networks entry. publisher says who posts for it: Buffer (absent or "buffer", with buffer_channel_id) or
 * Meta direct ("meta", with meta_page_id for a Facebook Page or ig_user_id for Instagram; docs/specs/META_DIRECT.md).
 */
export type SocialNetworkEntry = {
  network: string;
  account: string;
  buffer_channel_id?: string;
  name?: string;
  cadence_per_week?: number;
  publisher?: "buffer" | "meta";
  meta_page_id?: string;
  ig_user_id?: string;
};

/** Who posts for an entry, in words (Settings › Social). */
export function publisherLabel(n: SocialNetworkEntry): string {
  if (n.publisher === "meta") return `Meta direct: ${n.name ?? n.ig_user_id ?? n.meta_page_id ?? "account"}`;
  return n.buffer_channel_id ? `Buffer: ${n.name ?? n.buffer_channel_id}` : "No Buffer channel yet";
}

/** The picker's starting ticks: channels already in social.networks are ticked with their network and account. */
export function initialPicks(channels: BufferChannel[], networks: SocialNetworkEntry[]): Record<string, ChannelPick> {
  const out: Record<string, ChannelPick> = {};
  for (const c of channels) {
    const known = networks.find((n) => n.buffer_channel_id === c.id);
    const network = known?.network ?? c.network ?? "";
    out[c.id] = {
      on: Boolean(known),
      network,
      account: known?.account ?? (network ? DEFAULT_ACCOUNT[network as keyof typeof DEFAULT_ACCOUNT] ?? "business" : "business"),
    };
  }
  return out;
}

/**
 * The new social.networks from the ticked channels: each with its network, account, Buffer channel id and name. A
 * network kept from before keeps its own posts per week.
 */
export function networksFromPicks(channels: BufferChannel[], picks: Record<string, ChannelPick>, existing: SocialNetworkEntry[]): SocialNetworkEntry[] {
  const out: SocialNetworkEntry[] = [];
  for (const c of channels) {
    const p = picks[c.id];
    if (!p?.on || !p.network) continue;
    const before = existing.find((n) => n.buffer_channel_id === c.id) ?? existing.find((n) => n.network === p.network && n.account === p.account);
    const entry: SocialNetworkEntry = { network: p.network, account: p.account, buffer_channel_id: c.id, name: c.name };
    if (typeof before?.cadence_per_week === "number") entry.cadence_per_week = before.cadence_per_week;
    out.push(entry);
  }
  // Accounts posted to through Meta stay, unless a ticked Buffer channel now takes their network and account.
  const taken = new Set(out.map((n) => `${n.network}/${n.account}`));
  for (const n of existing) if (n.publisher === "meta" && !taken.has(`${n.network}/${n.account}`)) out.push(n);
  return out;
}

/** What is wrong with the ticks, before saving: a channel with no network, or two channels on one network and account. */
export function picksProblem(channels: BufferChannel[], picks: Record<string, ChannelPick>): string | null {
  const seen = new Map<string, string>();
  for (const c of channels) {
    const p = picks[c.id];
    if (!p?.on) continue;
    if (!p.network) return `Choose which network ${c.name} posts to.`;
    const k = `${p.network}/${p.account}`;
    if (seen.has(k)) return `${seen.get(k)} and ${c.name} are both ${NETWORK_LABELS[p.network as keyof typeof NETWORK_LABELS] ?? p.network} (${p.account.replace("_", " ")}). Give one a different account.`;
    seen.set(k, c.name);
  }
  return null;
}

export function hasBufferChannels(config: JsonObject | null | undefined): boolean {
  const n = at(config, ["social", "networks"]);
  return Array.isArray(n) && n.some((x) => !!x && typeof x === "object" && !Array.isArray(x) && typeof (x as JsonObject).buffer_channel_id === "string");
}

/** 0.12 At least one network is posted to through Meta direct. */
export function hasMetaChannels(config: JsonObject | null | undefined): boolean {
  const n = at(config, ["social", "networks"]);
  return Array.isArray(n) && n.some((x) => !!x && typeof x === "object" && !Array.isArray(x) && (x as JsonObject).publisher === "meta");
}

/** The social networks are set: a Buffer channel or a Meta account is chosen. */
export function hasSocialChannels(config: JsonObject | null | undefined): boolean {
  return hasBufferChannels(config) || hasMetaChannels(config);
}
